import { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import PageHeader from "../../components/PageHeader";

type Row = {
  id: string;
  word: string;
  categoryKey: string;
  letter: string;
  count: number;
  lastAtMs?: number;
};

function normalize(s: string): string {
  return s
    .trim()
    .replace(/I/g, "ı")
    .replace(/İ/g, "i")
    .toLowerCase()
    .replace(/̇/g, "");
}

function relative(ms?: number): string {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const m = Math.round(diff / 60000);
  if (m < 60) return `${m} dk önce`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} sa önce`;
  return `${Math.round(h / 24)} gün önce`;
}

/**
 * Oyunda reddedilen ama kabul edilmesi gereken cevapların kuyruğu.
 *
 * Uygulama, sorunun cevap listesinde bulunmayan her ciddi denemeyi
 * kaydediyor. Bu, listeyi tahminle doldurmaktan çok daha isabetli: gerçek
 * oyuncuların gerçekten yazdığı kelimeler, kaç kez denendiğine göre sıralı.
 *
 * "quiz:" öneksiz satırlar kelime havuzlu eski sürümden geliyor; o sürüm
 * hâlâ `categories` koleksiyonunu okuduğu için kabul yolu duruyor.
 */
export default function Rejected() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [catNames, setCatNames] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setBusy("load");
    setErr(null);
    try {
      const [rejSnap, catSnap, quizSnap] = await Promise.all([
        getDocs(collection(db, "rejectedAnswers")),
        getDocs(collection(db, "categories")),
        getDocs(collection(db, "quizQuestions")),
      ]);
      const names = new Map<string, string>();
      catSnap.docs.forEach((d) =>
        names.set(d.id, (d.data().name as string) ?? d.id)
      );
      // Soru bankası kayıtları "quiz:<soruId>" anahtarıyla geliyor; kuyrukta
      // kategori adı yerine sorunun kendisi görünsün.
      quizSnap.docs.forEach((d) =>
        names.set(`quiz:${d.id}`, (d.data().text as string) ?? d.id)
      );
      setCatNames(names);
      setRows(
        rejSnap.docs
          .map((d) => {
            const x = d.data();
            return {
              id: d.id,
              word: (x.word as string) ?? "",
              categoryKey: (x.categoryKey as string) ?? "",
              letter: (x.letter as string) ?? "",
              count: Number(x.count ?? 1),
              lastAtMs: x.lastAt?.toMillis?.(),
            };
          })
          .sort((a, b) => b.count - a.count)
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  /**
   * Kelimeyi doğru yere ekler ve kuyruktan düşürür.
   *
   * İki kaynak var: kelime havuzu (kategori) ve soru bankası. Hangisinden
   * geldiği `categoryKey`'in "quiz:" önekinden anlaşılıyor.
   */
  const accept = async (r: Row) => {
    setBusy(r.id);
    setErr(null);
    try {
      const w = normalize(r.word);

      if (r.categoryKey.startsWith("quiz:")) {
        const qid = r.categoryKey.slice(5);
        const ref = doc(db, "quizQuestions", qid);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error("Soru bulunamadı");
        const answers: string[] = (snap.data().answers as string[]) ?? [];
        // Eşanlamlılar dahil kontrol — aynı cevap ikinci kez girmesin.
        const known = new Set<string>();
        answers.forEach((line) =>
          line.split("|").forEach((f) => known.add(f))
        );
        if (!known.has(w)) {
          const next = [...answers, w];
          await setDoc(
            ref,
            {
              answers: next,
              answerCount: next.length,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      } else {
        const ref = doc(db, "categories", r.categoryKey);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error("Kategori bulunamadı");
        const words: string[] = (snap.data().words as string[]) ?? [];
        if (!words.includes(w)) {
          const next = Array.from(new Set([...words, w])).sort((a, b) =>
            a.localeCompare(b, "tr")
          );
          await setDoc(
            ref,
            {
              words: next,
              wordCount: next.length,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      }

      await deleteDoc(doc(db, "rejectedAnswers", r.id));
      setRows((prev) => prev?.filter((x) => x.id !== r.id) ?? null);
      setMsg(`"${r.word}" → ${catNames.get(r.categoryKey) ?? r.categoryKey}`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (r: Row) => {
    setBusy(r.id);
    try {
      await deleteDoc(doc(db, "rejectedAnswers", r.id));
      setRows((prev) => prev?.filter((x) => x.id !== r.id) ?? null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const shown = useMemo(() => {
    if (!rows) return [];
    const q = normalize(filter);
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.word.includes(q) ||
        normalize(catNames.get(r.categoryKey) ?? r.categoryKey).includes(q)
    );
  }, [rows, filter, catNames]);

  const totalTries = rows?.reduce((s, r) => s + r.count, 0) ?? 0;

  return (
    <div className="pb-24">
      <PageHeader
        title="Reddedilenler"
        subtitle="Oyuncuların yazdığı ama havuzda olmayan cevaplar. Doğru harfle başlayanlar kaydediliyor — yani listede eksik olanlar."
      />

      {err && (
        <div className="card p-4 mb-4 border-danger/40 text-danger text-sm flex items-start gap-3">
          <span>⚠️</span>
          <span className="flex-1">{err}</span>
          <button className="btn-ghost text-xs" onClick={() => setErr(null)}>
            Kapat
          </button>
        </div>
      )}
      {msg && (
        <div className="card p-4 mb-4 border-success/40 text-success text-sm flex items-center gap-3">
          <span className="flex-1">Eklendi: {msg}</span>
          <button className="btn-ghost text-xs" onClick={() => setMsg(null)}>
            Kapat
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <div className="card p-4">
          <div className="text-2xl font-extrabold leading-none">
            {rows?.length ?? "—"}
          </div>
          <div className="text-[11px] text-textMuted mt-1.5">bekleyen kayıt</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-extrabold leading-none text-warning">
            {totalTries || "—"}
          </div>
          <div className="text-[11px] text-textMuted mt-1.5">
            toplam deneme
          </div>
        </div>
        <div className="card p-4 flex items-center">
          <button
            className="btn-outline w-full py-2 text-sm"
            disabled={busy !== null}
            onClick={load}
          >
            Yenile
          </button>
        </div>
      </div>

      <input
        className="input mb-4 py-2.5 text-sm"
        placeholder="Kelime veya kategori ara…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {rows && rows.length === 0 && (
        <div className="card p-10 text-center">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-textSecondary text-sm">
            Kuyruk boş. Oyuncular havuzda olmayan bir cevap yazdığında burada
            görünecek.
          </p>
        </div>
      )}

      {shown.length > 0 && (
        <div className="card divide-y divide-stroke">
          {shown.map((r) => (
            <div
              key={r.id}
              className="flex flex-wrap items-center gap-3 px-4 py-3"
            >
              <span
                className="w-8 h-8 rounded-lg bg-surfaceHigh flex items-center justify-center text-sm font-extrabold uppercase shrink-0"
                title={
                  r.categoryKey.startsWith("quiz:")
                    ? "Soru bankası"
                    : "Kelime havuzu"
                }
              >
                {r.categoryKey.startsWith("quiz:") ? "🧠" : r.letter}
              </span>
              <div className="flex-1 min-w-[140px]">
                <div className="font-semibold text-sm">{r.word}</div>
                <div className="text-[11px] text-textMuted">
                  {catNames.get(r.categoryKey) ?? r.categoryKey} ·{" "}
                  {relative(r.lastAtMs)}
                </div>
              </div>
              <span
                className={`pill tabular-nums ${
                  r.count >= 3
                    ? "bg-warning/20 text-warning"
                    : "bg-surfaceHigh text-textMuted"
                }`}
              >
                {r.count} deneme
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="btn-primary py-2 px-4 text-sm"
                  disabled={busy !== null}
                  onClick={() => accept(r)}
                >
                  {busy === r.id ? "…" : "Ekle"}
                </button>
                <button
                  className="btn-ghost text-sm"
                  disabled={busy !== null}
                  onClick={() => dismiss(r)}
                  title="Havuza ait değil — kuyruktan kaldır"
                >
                  Yok say
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
